import { useNavigate } from "react-router";
import { Routes } from "@/config/routes";
import { auth } from "@/endpoints/dryp/auth";
import { useAsyncStateStore } from "@/hooks/useAsyncStateStore";
import { useStore } from "@/hooks/useStore";
import { UserStorage } from "@/storage/UserStorage";
import { z } from "zod";
import { validation } from "@/domain/validation";
import { useFormHandler } from "@/hooks/useFormHandler";

/** 🔹 Login form validation schema */
const schema = z.object({
	Email: validation.email,
	Password: validation.password,
});

/** 🔹 Infer form values type */
type LoginFormValues = z.infer<typeof schema>;

/**
 * ✅ Custom hook to manage login logic:
 * - Handles form validation & submission
 * - Calls authentication APIs
 * - Manages user session state
 */
export const useLogin = () => {
	const { userStore } = useStore();
	const loginApi = useAsyncStateStore(auth.login);
	const getUserAndOrganizationApi = useAsyncStateStore(auth.getUserAndOrganization);
	const navigate = useNavigate();

	/** 🔹 Form handler with schema validation */
	const { form, handleFormErrors } = useFormHandler(schema, {
		defaultValues: { Email: "", Password: "" },
	});

	/** ✅ Logs in the user & stores auth token */
	const handleLogin = async (data: LoginFormValues) => {
		const response = await loginApi.fetch({ email: data.Email, password: data.Password });
		UserStorage.setToken(response.Token);
		UserStorage.setRefreshToken(response.RefreshToken);
		return response;
	};

	/** ✅ Fetches user & organization data after login */
	const fetchUserAndOrganization = async () => {
		const userAndOrganization = await getUserAndOrganizationApi.fetch();
		if (!userAndOrganization) {
			UserStorage.removeToken();
		} else {
			userStore.setUserAndOrganization(
				userAndOrganization.user,
				userAndOrganization.organization,
				userAndOrganization.organizations,
			);
			userStore.setLoggedIn();
		}
	};

	/** ✅ Handles form submission */
	const onSubmit = async (data: LoginFormValues) => {
		try {
			await handleLogin(data);
			await fetchUserAndOrganization();
			navigate(Routes.Dashboard.Home.path);
		} catch (error) {
			handleFormErrors(error);
		}
	};

	/** 🔹 UI loading state & error handling */
	const isLoading = loginApi.loading || getUserAndOrganizationApi.loading;
	const error =
		getUserAndOrganizationApi.loaded && !getUserAndOrganizationApi.data
			? { message: "Login failed. Please try again later" }
			: loginApi.error;

	return { form, onSubmit, isLoading, error };
};
